import ApiService from "./ApiService";
import { LOG_SEVERITIES, USER_TYPES } from "../assets/appConstants";
import { normalizeAccountTypeHint } from "./utils";

const normalizeAccountType = (type) => normalizeAccountTypeHint(type);

export const isClinicianAccountType = (accountType) => {
  const normalized = normalizeAccountType(accountType);
  return normalized === USER_TYPES.THERAPIST || normalized === USER_TYPES.CLINICIAN;
};

const emptyContext = (accountType = "") => ({
  accountType: accountType || "",
  defaultSubAccountId: null,
  needToSetPassword: undefined,
  registrationCompleted: undefined,
  subAccountStatus: undefined,
});

const normalizeMyTypeResponse = (myType = {}) => {
  const raw = myType?.type || myType?.accountType || "";
  const normalized = String(raw).toUpperCase();
  let accountType = normalizeAccountType(raw);

  if (normalized === "THERAPIST" || normalized === "CLINICIAN") {
    accountType = USER_TYPES.THERAPIST;
  } else if (normalized === "PATIENT") {
    accountType = USER_TYPES.PATIENT;
  }

  return {
    accountType,
    defaultSubAccountId:
      myType?.defaultSubAccountId ??
      myType?.subAccountId ??
      myType?.account?.defaultSubAccountId ??
      myType?.account?.subAccountId ??
      null,
    needToSetPassword:
      myType?.needToSetPassword ?? myType?.account?.needToSetPassword,
    registrationCompleted:
      myType?.registrationCompleted ?? myType?.account?.registrationCompleted,
    subAccountStatus:
      myType?.subAccountStatus ||
      myType?.status ||
      myType?.account?.status,
  };
};

const normalizeClaimsContext = (claims = {}) => ({
  accountType: normalizeAccountType(claims?.accountType),
  defaultSubAccountId: claims?.defaultSubAccountId ?? claims?.subAccountId ?? null,
  needToSetPassword: claims?.needToSetPassword,
  registrationCompleted: claims?.registrationCompleted,
  subAccountStatus: claims?.subAccountStatus || claims?.status,
});

export const getAccountTypeFromCustomClaims = async (user) => {
  const context = await getAccountContextFromCustomClaims(user);
  return context.accountType;
};

export const getAccountContextFromCustomClaims = async (user) => {
  if (!user?.getIdTokenResult) {
    return emptyContext();
  }
  try {
    // Do not force refresh — token was just obtained during login.
    const { claims } = await user.getIdTokenResult(false);
    return normalizeClaimsContext(claims);
  } catch (e) {
    ApiService.logIt(
      "failed to read accountType from custom claims",
      LOG_SEVERITIES.NOTICE,
      e
    );
    return emptyContext();
  }
};

export const fetchAccountMyTypeContext = async () => {
  try {
    const myType = await ApiService.getAccountMyType();
    return normalizeMyTypeResponse(myType);
  } catch (e) {
    ApiService.logIt("accounts/my/type unavailable", LOG_SEVERITIES.NOTICE, e);
    return emptyContext();
  }
};

const mergeAccountContext = (hinted, claimsContext, myTypeContext) => {
  // Prefer API/claims type over URL hint so LOGIN_SUCCESS keeps canonical
  // "therapist" (parent apps do not understand URL hint "clinician").
  const fromApi = claimsContext.accountType || myTypeContext.accountType || "";
  const fromHint =
    hinted === USER_TYPES.CLINICIAN ? USER_TYPES.THERAPIST : hinted || "";
  const accountType = fromApi || fromHint || "";

  return {
    accountType,
    defaultSubAccountId:
      claimsContext.defaultSubAccountId ?? myTypeContext.defaultSubAccountId ?? null,
    needToSetPassword:
      claimsContext.needToSetPassword ?? myTypeContext.needToSetPassword,
    registrationCompleted:
      claimsContext.registrationCompleted ?? myTypeContext.registrationCompleted,
    subAccountStatus:
      claimsContext.subAccountStatus ?? myTypeContext.subAccountStatus,
  };
};

/**
 * Lightweight post-auth account context for login routing.
 * Calls GET /v2/accounts/my/type at most once per invocation.
 */
export const resolveAccountContext = async (user, accountTypeHint = "") => {
  const hinted = normalizeAccountType(accountTypeHint);

  // Patient with a clear app hint: no need for my/type during login routing.
  if (hinted === USER_TYPES.PATIENT) {
    return emptyContext(hinted);
  }

  // Clinician hint: still need my/type for defaultSubAccountId / flags.
  // Unknown hint: need my/type (and claims) to decide patient vs clinician.
  const claimsContext = hinted
    ? emptyContext()
    : await getAccountContextFromCustomClaims(user);
  const myTypeContext = await fetchAccountMyTypeContext();

  return mergeAccountContext(hinted, claimsContext, myTypeContext);
};

/**
 * Resolve account type: URL/app hint → Firebase custom claims → GET /v2/accounts/my/type.
 */
export const resolveAccountType = async (user, accountTypeHint = "") => {
  const context = await resolveAccountContext(user, accountTypeHint);
  return context.accountType;
};
